import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  composeDateInputValue,
  parsePastedDate,
  splitDateInputValue,
} from '../formatDate';

interface DateOfBirthInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
}

type Segment = 'day' | 'month' | 'year';

const SEGMENT_MAX: Record<Segment, number> = { day: 2, month: 2, year: 4 };
const NEXT_SEGMENT: Partial<Record<Segment, Segment>> = { day: 'month', month: 'year' };
const PREV_SEGMENT: Partial<Record<Segment, Segment>> = { month: 'day', year: 'month' };

export const DateOfBirthInput: React.FC<DateOfBirthInputProps> = ({
  value,
  onChange,
  disabled = false,
  required = false,
  invalid = false,
}) => {
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const [parts, setParts] = useState(() => splitDateInputValue(value));

  useEffect(() => {
    const composed = composeDateInputValue(parts.day, parts.month, parts.year);
    if (value !== composed) {
      setParts(splitDateInputValue(value));
    }
  }, [value, parts.day, parts.month, parts.year]);

  const focusSegment = useCallback((segment: Segment) => {
    const el =
      segment === 'day' ? dayRef.current : segment === 'month' ? monthRef.current : yearRef.current;
    el?.focus();
    el?.select();
  }, []);

  const emitChange = useCallback(
    (next: { day: string; month: string; year: string }) => {
      setParts(next);
      onChange(composeDateInputValue(next.day, next.month, next.year));
    },
    [onChange],
  );

  const handleSegmentChange = useCallback(
    (segment: Segment, raw: string) => {
      const digits = raw.replace(/\D/g, '').slice(0, SEGMENT_MAX[segment]);
      const next = { ...parts, [segment]: digits };
      emitChange(next);
      if (digits.length === SEGMENT_MAX[segment] && NEXT_SEGMENT[segment]) {
        focusSegment(NEXT_SEGMENT[segment]!);
      }
    },
    [parts, emitChange, focusSegment],
  );

  const handleKeyDown = useCallback(
    (segment: Segment, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && parts[segment] === '') {
        const prev = PREV_SEGMENT[segment];
        if (prev) {
          e.preventDefault();
          focusSegment(prev);
        }
      }
    },
    [parts, focusSegment],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const parsed = parsePastedDate(e.clipboardData.getData('text'));
      if (!parsed) return;
      e.preventDefault();
      emitChange(parsed);
      yearRef.current?.focus();
    },
    [emitChange],
  );

  const segmentProps = (segment: Segment, placeholder: string, className: string) => ({
    ref: segment === 'day' ? dayRef : segment === 'month' ? monthRef : yearRef,
    type: 'text' as const,
    inputMode: 'numeric' as const,
    maxLength: SEGMENT_MAX[segment],
    placeholder,
    className,
    value: parts[segment],
    disabled,
    required,
    'aria-label': placeholder,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleSegmentChange(segment, e.target.value),
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(segment, e),
    onPaste: handlePaste,
  });

  return (
    <div className={`dob-input${invalid ? ' dob-input--invalid' : ''}`}>
      <input {...segmentProps('day', 'DD', 'dob-input__segment dob-input__segment--day')} />
      <span className="dob-input__separator" aria-hidden="true">.</span>
      <input {...segmentProps('month', 'MM', 'dob-input__segment dob-input__segment--month')} />
      <span className="dob-input__separator" aria-hidden="true">.</span>
      <input {...segmentProps('year', 'YYYY', 'dob-input__segment dob-input__segment--year')} />
    </div>
  );
};
